import { useRef, type KeyboardEvent, type ReactNode, type Ref } from "react";
import { ArrowRightOutlined, CheckOutlined, InfoCircleOutlined, TeamOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { OpenTable, Reservation } from "../../../../api/reservations";
import { useViewport } from "../../../../hooks/useViewport";
import { fmtEuro } from "../../../../utils/currency";
import {
  canConfirmSeatBills,
  crowdedBillsOf,
  separatePlanOf,
  type SeatBillsChoice,
  type SeatBillsMode,
} from "../../logic/seatBills";
import ReservationConfirmModal from "../confirm/ReservationConfirmModal";
import SeatBillRow from "./SeatBillRow";
import SeatReservationTicket from "./SeatReservationTicket";

/** Отметката вдясно на избраната възможност/сметка. */
const CheckMark = ({ checked }: { checked: boolean }) => (
  <span
    className={`flex size-[22px] shrink-0 items-center justify-center rounded-full border text-[11px] text-white ${
      checked ? "border-brand bg-brand" : "border-shade-5 bg-surface"
    }`}
  >
    {checked && <CheckOutlined />}
  </span>
);

/** Една възможност — „Една сметка" / „Отделна сметка за всяка маса". */
const ModeOption = ({
  optionRef,
  checked,
  disabled,
  title,
  description,
  warn,
  onSelect,
}: {
  optionRef: Ref<HTMLButtonElement>;
  checked: boolean;
  disabled: boolean;
  title: string;
  description: string;
  // Описанието е причината възможността да е спряна.
  warn: boolean;
  onSelect: () => void;
}) => (
  <button
    ref={optionRef}
    type="button"
    role="radio"
    aria-checked={checked}
    disabled={disabled}
    onClick={onSelect}
    className={`flex w-full cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-brand/25 disabled:cursor-not-allowed ${
      checked ? "border-brand bg-brand-tint" : "border-shade-3 bg-surface hover:bg-fill"
    }`}
  >
    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
      <span className={`text-[14.5px] font-bold leading-[1.3] ${disabled && !checked ? "text-muted" : "text-ink"}`}>
        {title}
      </span>
      <span
        className={`flex items-start gap-1.5 text-[12.5px] leading-[1.4] ${
          warn ? "font-semibold text-warn-ink" : "font-medium text-body"
        }`}
      >
        {warn && <InfoCircleOutlined className="mt-0.5 shrink-0" />}
        <span className="min-w-0">{description}</span>
      </span>
    </span>
    <CheckMark checked={checked} />
  </button>
);

/** Заглавие на част от избора (микро-етикет). */
const Section = ({ label, children }: { label: string; children: ReactNode }) => (
  <section className="flex flex-col gap-2">
    <h3 className="m-0 text-[10.5px] font-bold uppercase tracking-[1.2px] text-muted">{label}</h3>
    {children}
  </section>
);

/** Ред от „Какво ще стане": масите → нова сметка или отворената (null). */
interface OutcomeRow {
  tables: string[];
  // Масата на новата сметка, ако се отваря нова.
  newTable: string | null;
  bill: OpenTable | null;
  // На кого е новата сметка (null — „ничия" до първата поръчка).
  owner: string | null;
}

/**
 * „Настани" на резервация с няколко маси — подредбата на „Настани" от
 * Маркиране: вляво каква е резервацията (билетът — SeatReservationTicket),
 * вдясно изборът — „Как да се водят сметките" (една, по подразбиране, или
 * отделна за всяка маса; без интернет „Отделна…" е спряна — касата отваря
 * една сметка), „Към коя сметка" при няколко отворени сметки (при „Една
 * сметка" — между всички; при „Отделна…" — само на масите с няколко, напр.
 * двама сервитьори на маса 25: избраният взима и другите маси —
 * `separatePlanOf`) и „Какво ще стане" — масите → нова или отворената
 * сметка. Шелът и футърът са на `ReservationConfirmModal` („Назад" /
 * „Настани"); Enter в избора потвърждава (Enter на сметка — избира я и
 * настанява), ↑/↓ сменят „Една сметка" / „Отделна…", Esc = „Назад" (с
 * preventDefault — в шела). На телефон и таблет резервацията е над избора.
 */
const SeatBillsModal = ({
  reservation,
  choice,
  mode,
  pickedBillId,
  nowMs,
  pending,
  onModeChange,
  onPickBill,
  onConfirm,
  onBack,
}: {
  // null — модалът е затворен.
  reservation: Reservation | null;
  choice: SeatBillsChoice | null;
  mode: SeatBillsMode;
  pickedBillId: number | null;
  nowMs: number;
  pending: boolean;
  onModeChange: (mode: SeatBillsMode) => void;
  onPickBill: (bill: OpenTable) => void;
  // `bill` — сметката от Enter върху ред (още не е в състоянието).
  onConfirm: (bill?: OpenTable) => void;
  onBack: () => void;
}) => {
  const { t } = useTranslation();
  const { isMobile, isTablet } = useViewport();
  const stacked = isMobile || isTablet;
  const singleRef = useRef<HTMLButtonElement>(null);
  const separateRef = useRef<HTMLButtonElement>(null);
  const open = reservation !== null && choice !== null;

  const allPickBills = choice?.single.kind === "pick" ? choice.single.bills : [];
  // „Към коя сметка": при „Една сметка" — всички; при „Отделна…" — само на
  // масите с няколко (другите маси си имат своята).
  const pickBills = mode === "separate" && choice ? crowdedBillsOf(choice) : allPickBills;
  const pickedBill = allPickBills.find((bill) => bill.billId === pickedBillId) ?? null;
  const canConfirm = choice !== null && canConfirmSeatBills(choice, mode, pickedBill);
  const separateDisabled = choice?.separateBlock != null;
  const tableNames = choice?.tables.map((entry) => entry.tableName) ?? [];
  const crowdedTables = choice?.tables.filter((entry) => entry.bills.length > 1) ?? [];
  const separateHint = separateDisabled ? t("reservations.seatBills.separateLocalMode") : null;

  // „Какво ще стане": при „Една сметка" — всички маси на един ред; при
  // „Отделна…" — ред за всяка маса. `outcomeNote` — вместо редовете.
  const singleRow = ((): OutcomeRow | null => {
    if (!choice) return null;
    const { single } = choice;
    if (single.kind === "newBill") return { tables: tableNames, newTable: choice.singleTable, bill: null, owner: null };
    if (single.kind === "attach") return { tables: tableNames, newTable: null, bill: single.bill, owner: null };
    return pickedBill ? { tables: tableNames, newTable: null, bill: pickedBill, owner: null } : null;
  })();
  const separatePlan = choice && mode === "separate" ? separatePlanOf(choice, pickedBill) : null;
  const outcomeRows: OutcomeRow[] =
    separatePlan?.kind === "ready"
      ? separatePlan.bills.map((entry) => ({
          tables: [entry.tableName],
          newTable: entry.bill ? null : entry.tableName,
          bill: entry.bill,
          owner: separatePlan.owner?.waiterName ?? null,
        }))
      : mode === "single" && singleRow
        ? [singleRow]
        : [];
  const outcomeNote =
    mode === "separate"
      ? (separateHint ??
        (separatePlan?.kind === "pick"
          ? t("reservations.seatBills.pickOnTable", { table: separatePlan.table })
          : separatePlan?.kind === "noBillOfWaiter"
            ? t("reservations.seatBills.noBillOfWaiter", {
                table: separatePlan.table,
                waiter: separatePlan.waiter ?? t("reservations.seatPicker.unknownWaiter"),
              })
            : null))
      : singleRow === null
        ? t("reservations.seatBills.singlePick")
        : null;

  const selectMode = (next: SeatBillsMode) => {
    if (next === "separate" && separateDisabled) return;
    onModeChange(next);
    (next === "single" ? singleRef : separateRef).current?.focus();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (pending) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      selectMode(event.key === "ArrowDown" ? "separate" : "single");
      return;
    }
    if (event.key !== "Enter") return;
    event.preventDefault();
    const rowBillId = (event.target as HTMLElement).closest<HTMLElement>("[data-seat-bill]")?.dataset.seatBill;
    const rowBill = allPickBills.find((bill) => String(bill.billId) === rowBillId);
    if (rowBill) {
      onConfirm(rowBill);
      return;
    }
    if (canConfirm) onConfirm();
  };

  const tablesLabel = (names: string[]) =>
    t("seatReservation.tables", { count: names.length, tables: names.join(", ") });

  return (
    <ReservationConfirmModal
      open={open}
      tone="primary"
      icon={<TeamOutlined />}
      title={reservation ? t("seatReservation.choice.title", { guest: reservation.guestName }) : ""}
      size="wide"
      flush
      confirmLabel={t("reservations.seatBills.confirm")}
      backLabel={t("reservations.seatBills.back")}
      pending={pending}
      confirmDisabled={!canConfirm}
      initialFocusRef={singleRef}
      onConfirm={() => onConfirm()}
      onBack={onBack}
    >
      {reservation && choice && (
        <div className={stacked ? "flex flex-col" : "flex items-stretch"}>
          <div className={stacked ? "border-b border-shade-2 bg-panel px-4 py-3" : "w-[36%] shrink-0 bg-panel px-5 py-5"}>
            <SeatReservationTicket reservation={reservation} tableNames={tableNames} compact={stacked} />
          </div>
          <div
            onKeyDown={handleKeyDown}
            className={`flex min-w-0 flex-1 flex-col gap-5 ${stacked ? "p-4" : "border-l border-shade-2 px-5 py-5"}`}
          >
            <Section label={t("seatReservation.bills.label")}>
              <div role="radiogroup" aria-label={t("reservations.seatBills.group")} className="flex flex-col gap-2">
                <ModeOption
                  optionRef={singleRef}
                  checked={mode === "single"}
                  disabled={pending}
                  title={t("reservations.seatBills.single")}
                  description={t("seatReservation.bills.singleSummary")}
                  warn={false}
                  onSelect={() => selectMode("single")}
                />
                <ModeOption
                  optionRef={separateRef}
                  checked={mode === "separate"}
                  disabled={pending || separateDisabled}
                  title={t("reservations.seatBills.separate")}
                  description={separateHint ?? t("reservations.seatBills.separateSummary", { count: tableNames.length })}
                  warn={separateHint !== null}
                  onSelect={() => selectMode("separate")}
                />
              </div>
            </Section>

            {pickBills.length > 0 && (
              <Section
                label={
                  mode === "separate" && crowdedTables.length === 1
                    ? t("seatReservation.choice.pickBillOnTable", { table: crowdedTables[0].tableName })
                    : t("seatReservation.choice.pickBill")
                }
              >
                <div className="flex flex-col gap-2">
                  {pickBills.map((bill) => (
                    <div key={bill.billId} data-seat-bill={bill.billId}>
                      <SeatBillRow
                        bill={bill}
                        nowMs={nowMs}
                        selected={bill.billId === pickedBillId}
                        disabled={pending}
                        onClick={() => onPickBill(bill)}
                        trailing={<CheckMark checked={bill.billId === pickedBillId} />}
                      />
                    </div>
                  ))}
                </div>
              </Section>
            )}

            <Section label={t("seatReservation.choice.outcome")}>
              {outcomeNote !== null ? (
                <p className="m-0 rounded-xl border border-dashed border-shade-4 px-4 py-3 text-[13px] font-medium leading-[1.4] text-body">
                  {outcomeNote}
                </p>
              ) : (
                <ul className="m-0 list-none overflow-hidden rounded-xl border border-shade-3 bg-surface p-0">
                  {outcomeRows.map((row) => (
                    <li
                      key={row.tables.join(",")}
                      className="flex items-center gap-3 border-t border-shade-2 px-4 py-2.5 first:border-t-0"
                    >
                      <span className="w-[96px] shrink-0 break-words text-[13px] font-semibold leading-[1.3] text-ink-2">
                        {tablesLabel(row.tables)}
                      </span>
                      <ArrowRightOutlined className="shrink-0 text-[11px] !text-brand" />
                      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <span
                          className={`text-[10.5px] font-bold uppercase tracking-[1.2px] ${
                            row.bill ? "text-muted" : "text-brand-deep-ink"
                          }`}
                        >
                          {t(row.bill ? "seatReservation.choice.open" : "seatReservation.choice.opens")}
                        </span>
                        <span className="break-words text-[14px] font-bold leading-[1.3] text-ink">
                          {row.bill
                            ? tablesLabel([row.bill.tableName])
                            : t("seatReservation.choice.newTable", { table: row.newTable ?? "" })}
                          {(row.bill ? row.bill.waiterName : row.owner) && (
                            <span className="font-medium text-body">
                              {" "}
                              {t("seatReservation.choice.onWaiter", { waiter: row.bill?.waiterName ?? row.owner })}
                            </span>
                          )}
                        </span>
                      </span>
                      {row.bill && (
                        <span className="shrink-0 text-[13.5px] font-bold tabular-nums text-ink">
                          {fmtEuro(row.bill.totalAmount)}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          </div>
        </div>
      )}
    </ReservationConfirmModal>
  );
};

export default SeatBillsModal;
